import {
  useEffect,
  useRef,
} from "react";

import {
  toast,
} from "react-toastify";

import { getApiBase } from "./api/healthThreatsApi";

const API_BASE = getApiBase();

const JOB_CHANNELS = [
  {
    scope: "biological",
    pagePath: "/biological",

    activeKey:
      "biological_active_training_job",

    lastResultKey:
      "biological_last_training_result",

    seenToastsKey:
      "biological_training_toast_seen",
  },
  {
    scope: "chemical",
    pagePath: "/chemical",

    activeKey:
      "chemical_active_training_job",

    lastResultKey:
      "chemical_last_training_result",

    seenToastsKey:
      "chemical_training_toast_seen",
  },
  {
    scope: "radiological-nuclear",
    pagePath: "/radiationnuclear",

    activeKey:
      "rn_active_training_job",

    lastResultKey:
      "rn_last_training_result",

    seenToastsKey:
      "rn_training_toast_seen",
  },
];

async function apiErrorToMessage(response) {
  const fallback =
    `Request failed (${response.status})`;

  try {
    const contentType =
      response.headers.get("content-type") || "";

    if (contentType.includes("application/json")) {
      const payload = await response
        .json()
        .catch(() => null);

      if (!payload) {
        return fallback;
      }

      const detail =
        payload.detail
        ?? payload.message
        ?? payload.error
        ?? payload;

      return typeof detail === "string"
        ? detail
        : JSON.stringify(detail);
    }

    const text = await response
      .text()
      .catch(() => "");

    return text.trim() || fallback;
  } catch {
    return fallback;
  }
}

function loadJson(key, fallback = null) {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function saveJson(key, value) {
  try {
    sessionStorage.setItem(
      key,
      JSON.stringify(value),
    );
  } catch {
    // Ignore unavailable storage.
  }
}

function removeStoredValue(key) {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // Ignore unavailable storage.
  }
}

function hasSeenToast(channel, jobId) {
  const seen = loadJson(
    channel.seenToastsKey,
    {},
  );

  return Boolean(seen?.[jobId]);
}

function markToastSeen(channel, jobId) {
  const seen = loadJson(
    channel.seenToastsKey,
    {},
  );

  seen[jobId] = true;
  saveJson(channel.seenToastsKey, seen);
}

function saveLastTrainingResult(
  channel,
  activeJob,
  job,
  status,
  message,
) {
  saveJson(channel.lastResultKey, {
    jobId: job.job_id || activeJob.jobId,
    country: job.country || activeJob.country,
    disease: job.threat || activeJob.disease,
    status,
    completedAt: new Date().toISOString(),
    message,
    datasetId: activeJob.datasetId || null,
    datasetSource: activeJob.datasetSource || null,
    trainingSource: activeJob.trainingSource || null,
  });
}

async function pollChannel(channel) {
  const activeJob = loadJson(channel.activeKey);

  if (!activeJob?.jobId) {
    return;
  }

  const currentPath =
    window.location.pathname;

  if (
    channel.pagePath
    && currentPath.includes(
      channel.pagePath
    )
  ) {
    /*
     * The active page already performs
     * live polling and displays progress.
     * The global watcher resumes after
     * navigation away from that page.
     */
    return;
  }

  try {
    const response = await fetch(
      `${API_BASE}/train/jobs/${activeJob.jobId}`,
      { cache: "no-store" },
    );

    if (!response.ok) {
      throw new Error(
        await apiErrorToMessage(response),
      );
    }

    const job = await response.json();

    const label =
      `${job.threat || activeJob.disease} - ${
        job.country || activeJob.country
      }`;

    if (job.status === "completed") {
      const message =
        job.message
        || `Training completed successfully for ${label}.`;

      if (!hasSeenToast(channel, activeJob.jobId)) {
        toast.success(message, {
          autoClose: false,
          closeOnClick: false,
          draggable: false,
          toastId:
            `${channel.scope}-training-completed-${activeJob.jobId}`,
        });

        markToastSeen(channel, activeJob.jobId);
      }

      saveLastTrainingResult(
        channel,
        activeJob,
        job,
        "completed",
        message,
      );

      removeStoredValue(channel.activeKey);
      return;
    }

    if (
      job.status === "failed"
      || job.status === "cancelled"
    ) {
      const message =
        job.error
        || job.message
        || `Training did not complete for ${label}.`;

      if (!hasSeenToast(channel, activeJob.jobId)) {
        toast.error(message, {
          autoClose: false,
          closeOnClick: false,
          draggable: false,
          toastId:
            `${channel.scope}-training-failed-${activeJob.jobId}`,
        });

        markToastSeen(channel, activeJob.jobId);
      }

      saveLastTrainingResult(
        channel,
        activeJob,
        job,
        job.status,
        message,
      );

      removeStoredValue(channel.activeKey);
    }
  } catch {
    /*
     * Keep the active job. A temporary network error
     * must not remove the stored job.
     */
  }
}

export default function TrainingJobWatcher() {
  const intervalRef = useRef(null);
  const pollingRef = useRef(false);

  useEffect(() => {
    const poll = async () => {
      if (pollingRef.current) {
        return;
      }

      pollingRef.current = true;

      try {
        await Promise.allSettled(
          JOB_CHANNELS.map(
            (channel) => pollChannel(channel),
          ),
        );
      } finally {
        pollingRef.current = false;
      }
    };

    poll();
    intervalRef.current = setInterval(
      poll,
      2500,
    );

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);

  return null;
}